import { request } from "umi";
import { paramsSerializer } from "../api";

const urlPrefix = "api/tenant/technician-availability";

/** One rule row of a job type limit (same shape as the automation Condition node) */
export interface JobTypeLimitCondition {
    field?: string;
    operator?: string;
    value?: any;
}

/**
 * Tenant-wide job type limit definition (settings
 * technician_availability.job_type_limits): a per-day cap for every job the
 * rule matches, e.g. "PSC" = dispatch type PSC from direct OR Frontdoor, 4/day.
 * max_per_day null = no tenant-wide value (technicians may still set one).
 */
export interface JobTypeLimitDefinition {
    key: string;
    label: string;
    max_per_day: number | null;
    rule: {
        logic: "AND" | "OR";
        conditions: JobTypeLimitCondition[];
    };
}

export interface TechnicianAvailabilityDefaults {
    max_jobs_per_day: number;
    work_time_from: string; // "HH:mm"
    work_time_to: string; // "HH:mm"
    work_days: number[]; // 0=Sunday .. 6=Saturday
    job_type_limits?: JobTypeLimitDefinition[];
}

/** A job type limit resolved for one technician (tenant value vs own value) */
export interface ResolvedJobTypeLimit {
    key: string;
    label: string;
    tenant_max: number | null;
    user_max: number | null;
    effective_max: number | null;
}

/** A job type limit resolved for one technician on one date */
export interface AvailabilityDayTypeLimit {
    key: string;
    label: string;
    /** This technician's jobs of that type on the day */
    job_count: number;
    max_jobs: number;
    /** True when max_jobs comes from a per-date override rule */
    is_override: boolean;
}

export interface TechnicianWorkSchedule {
    id: number;
    tenant_id: number;
    user_id: number;
    days_of_week: number[];
    time_from: string;
    time_to: string;
    effective_from: string;
    effective_to: string | null;
    created_at?: string;
    updated_at?: string;
}

export type TimeOffType = "time_off" | "vacation";

/** pending = requested by the user, awaiting approval (does not block the day yet) */
export type TimeOffStatus = "pending" | "approved" | "declined";

export interface TechnicianTimeOff {
    id: number;
    tenant_id: number;
    user_id: number;
    type: TimeOffType;
    date_from: string;
    date_to: string;
    time_from: string | null;
    time_to: string | null;
    note: string | null;
    status: TimeOffStatus;
    reviewed_by?: number | null;
    reviewed_at?: string | null;
    review_note?: string | null;
    reviewer?: { id: number; user_name?: string | null; display_name?: string | null } | null;
    created_by?: number | null;
    created_at?: string;
    updated_at?: string;
}

/** A time-off row on the manager's requests page, with the names resolved */
export interface TimeOffRequestRow extends TechnicianTimeOff {
    user_name: string;
    user_type: "employee" | "tenant" | null;
    requested_by_name: string | null;
    reviewed_by_name: string | null;
}

export interface TechnicianMaxJobsOverride {
    id: number;
    tenant_id: number;
    user_id: number;
    date_from: string; // Y-m-d
    date_to: string | null; // null = open-ended
    days_of_week: number[] | null; // null = every day in the range
    max_jobs: number;
    /** null = overrides the overall daily limit; otherwise a job type limit key */
    limit_key?: string | null;
    note: string | null;
    created_at?: string;
    updated_at?: string;
}

export type MaxJobsOverridePayload = Pick<TechnicianMaxJobsOverride, "date_from" | "date_to" | "days_of_week" | "max_jobs" | "limit_key" | "note">;

export interface TechnicianAvailability {
    user_id: number;
    user_name: string;
    /** 'employee' = technician, 'tenant' = office user */
    user_type?: "employee" | "tenant" | null;
    max_jobs_per_day: number | null;
    effective_max_jobs_per_day: number;
    /** This technician's own per-type values (missing key = tenant value) */
    max_jobs_by_type: Record<string, number>;
    /** Every tenant-defined job type limit resolved for this technician */
    job_type_limits: ResolvedJobTypeLimit[];
    tenant_defaults: TechnicianAvailabilityDefaults;
    schedules: TechnicianWorkSchedule[];
    time_off: TechnicianTimeOff[];
    max_jobs_overrides: TechnicianMaxJobsOverride[];
}

export interface AvailabilityDayTimeOff {
    id: number;
    type: TimeOffType;
    status?: TimeOffStatus;
    time_from: string | null;
    time_to: string | null;
    note: string | null;
}

export interface AvailabilityDayJob {
    dispatch_id: number;
    time_from: string | null;
    time_to: string | null;
    job_status: string | null;
    customer_name: string | null;
    /** Job type limit keys this job counts toward */
    limit_keys?: string[];
}

export interface AvailabilityDay {
    date: string;
    work: { time_from: string; time_to: string } | null;
    work_source: "schedule" | "default" | null;
    time_off: AvailabilityDayTimeOff[];
    /** Requests awaiting approval on this date; they never block the day */
    pending_time_off?: AvailabilityDayTimeOff[];
    off_full_day: boolean;
    /** Global tenant holiday name, or null. Holidays make the whole day unavailable. */
    holiday: string | null;
    /** Set when the technician rides as a HELPER with another technician that day (own route closed; off_full_day = true). */
    helper_for?: { route_helper_id: number; technician_user_id: number; technician_name: string | null } | null;
    job_count: number;
    max_jobs_per_day: number;
    /** True when max_jobs_per_day comes from a per-date override */
    max_is_override?: boolean;
    /** Overall cap OR any job type limit exceeded */
    over_capacity: boolean;
    /** Job type limits with an effective max on this date (empty when none) */
    type_limits?: AvailabilityDayTypeLimit[];
    jobs?: AvailabilityDayJob[];
}

export interface TechnicianCalendar {
    user_id: number;
    user_name: string;
    /** 'employee' = technician, 'tenant' = office user */
    user_type?: "employee" | "tenant" | null;
    user_color: string | null;
    max_jobs_per_day: number;
    /** Effective per-type max (tech value, else tenant value); keys without a value omitted */
    max_jobs_by_type?: Record<string, number>;
    has_custom_schedule: boolean;
    days: AvailabilityDay[];
}

/** Job type limits exceeded on a day for a job carrying `jobLimitKeys`.
 *  `excludeDispatchId` = the job being moved onto/within the day (not
 *  counted against itself; needs the day loaded with jobs). */
export function exceededTypeLimits(
    day: AvailabilityDay | null | undefined,
    jobLimitKeys: string[] | null | undefined,
    excludeDispatchId?: number,
): AvailabilityDayTypeLimit[] {
    if (!day?.type_limits?.length || !jobLimitKeys?.length) return [];
    const excludedKeys =
        excludeDispatchId !== undefined
            ? (day.jobs ?? []).find((j) => j.dispatch_id === excludeDispatchId)?.limit_keys ?? []
            : [];
    return day.type_limits
        .filter((limit) => jobLimitKeys.includes(limit.key))
        .map((limit) => ({
            ...limit,
            job_count: excludedKeys.includes(limit.key) ? Math.max(0, limit.job_count - 1) : limit.job_count,
        }))
        .filter((limit) => limit.job_count >= limit.max_jobs);
}

/** [GET] /api/tenant/technician-availability/{userId} */
export async function getTechnicianAvailability(userId: number) {
    return request<API.ResultObject<TechnicianAvailability>>(`${urlPrefix}/${userId}`, {
        method: "GET",
        withToken: true,
        paramsSerializer,
    }).then((res) => res.data);
}

export type WorkSchedulePayload = Pick<TechnicianWorkSchedule, "days_of_week" | "time_from" | "time_to" | "effective_from" | "effective_to">;

/** [POST] /api/tenant/technician-availability/{userId}/work-schedules */
export async function addWorkSchedule(userId: number, data: WorkSchedulePayload) {
    return request<API.ResultObject<TechnicianWorkSchedule>>(`${urlPrefix}/${userId}/work-schedules`, {
        method: "POST",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [PUT] /api/tenant/technician-availability/{userId}/work-schedules/{id} */
export async function updateWorkSchedule(userId: number, id: number, data: Partial<WorkSchedulePayload>) {
    return request<API.ResultObject<TechnicianWorkSchedule>>(`${urlPrefix}/${userId}/work-schedules/${id}`, {
        method: "PUT",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [DELETE] /api/tenant/technician-availability/{userId}/work-schedules/{id} */
export async function deleteWorkSchedule(userId: number, id: number) {
    return request<API.ResultObject<{ deleted: boolean }>>(`${urlPrefix}/${userId}/work-schedules/${id}`, {
        method: "DELETE",
        withToken: true,
    }).then((res) => res.data);
}

export type TimeOffPayload = Pick<TechnicianTimeOff, "type" | "date_from" | "date_to" | "time_from" | "time_to" | "note">;

/** [POST] /api/tenant/technician-availability/{userId}/time-off */
export async function addTimeOff(userId: number, data: TimeOffPayload) {
    return request<API.ResultObject<TechnicianTimeOff>>(`${urlPrefix}/${userId}/time-off`, {
        method: "POST",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [PUT] /api/tenant/technician-availability/{userId}/time-off/{id} */
export async function updateTimeOff(userId: number, id: number, data: Partial<TimeOffPayload>) {
    return request<API.ResultObject<TechnicianTimeOff>>(`${urlPrefix}/${userId}/time-off/${id}`, {
        method: "PUT",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [DELETE] /api/tenant/technician-availability/{userId}/time-off/{id} */
export async function deleteTimeOff(userId: number, id: number) {
    return request<API.ResultObject<{ deleted: boolean }>>(`${urlPrefix}/${userId}/time-off/${id}`, {
        method: "DELETE",
        withToken: true,
    }).then((res) => res.data);
}

/** [PUT] /api/tenant/technician-availability/{userId}/max-jobs
 *  Each field is only changed when passed: overall max (null = tenant
 *  default) and/or per-type values {key: n | null} (null = tenant value). */
export async function updateMaxJobs(
    userId: number,
    maxJobsPerDay?: number | null,
    maxJobsByType?: Record<string, number | null>,
) {
    const data: Record<string, any> = {};
    if (maxJobsPerDay !== undefined) data.max_jobs_per_day = maxJobsPerDay;
    if (maxJobsByType !== undefined) data.max_jobs_by_type = maxJobsByType;
    return request<API.ResultObject<{ max_jobs_per_day: number | null; max_jobs_by_type: Record<string, number> }>>(`${urlPrefix}/${userId}/max-jobs`, {
        method: "PUT",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [POST] /api/tenant/technician-availability/{userId}/max-jobs-overrides */
export async function addMaxJobsOverride(userId: number, data: MaxJobsOverridePayload) {
    return request<API.ResultObject<TechnicianMaxJobsOverride>>(`${urlPrefix}/${userId}/max-jobs-overrides`, {
        method: "POST",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [PUT] /api/tenant/technician-availability/{userId}/max-jobs-overrides/{id} */
export async function updateMaxJobsOverride(userId: number, id: number, data: Partial<MaxJobsOverridePayload>) {
    return request<API.ResultObject<TechnicianMaxJobsOverride>>(`${urlPrefix}/${userId}/max-jobs-overrides/${id}`, {
        method: "PUT",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [DELETE] /api/tenant/technician-availability/{userId}/max-jobs-overrides/{id} */
export async function deleteMaxJobsOverride(userId: number, id: number) {
    return request<API.ResultObject<{ deleted: boolean }>>(`${urlPrefix}/${userId}/max-jobs-overrides/${id}`, {
        method: "DELETE",
        withToken: true,
    }).then((res) => res.data);
}

/** [PUT] /api/tenant/technician-availability/{userId}/time-off/{id}/review — approve/decline a pending request */
export async function reviewTimeOff(userId: number, id: number, action: "approve" | "decline", note?: string | null) {
    return request<API.ResultObject<TechnicianTimeOff>>(`${urlPrefix}/${userId}/time-off/${id}/review`, {
        method: "PUT",
        data: { action, note: note ?? null },
        withToken: true,
    }).then((res) => res.data);
}

/** [GET] /api/tenant/technician-availability/time-off-requests — manager queue */
export async function getTimeOffRequests(params: {
    status?: TimeOffStatus | "all";
    user_type?: "employee" | "tenant" | "";
}) {
    return request<API.ResultObject<{ requests: TimeOffRequestRow[]; pending_count: number }>>(
        `${urlPrefix}/time-off-requests`,
        {
            method: "GET",
            params,
            withToken: true,
        },
    ).then((res) => res.data);
}

// ---------------------------------------------------------------------------
// Self-service (My Availability): the caller's own user. Work time repeats are
// edited directly; time off is a request that waits for approval.
// ---------------------------------------------------------------------------

const myPrefix = "api/tenant/my-availability";

/** [GET] /api/tenant/my-availability */
export async function getMyAvailability() {
    return request<API.ResultObject<TechnicianAvailability>>(myPrefix, {
        method: "GET",
        withToken: true,
    }).then((res) => res.data);
}

/** [POST] /api/tenant/my-availability/work-schedules */
export async function addMyWorkSchedule(data: WorkSchedulePayload) {
    return request<API.ResultObject<TechnicianWorkSchedule>>(`${myPrefix}/work-schedules`, {
        method: "POST",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [PUT] /api/tenant/my-availability/work-schedules/{id} */
export async function updateMyWorkSchedule(id: number, data: Partial<WorkSchedulePayload>) {
    return request<API.ResultObject<TechnicianWorkSchedule>>(`${myPrefix}/work-schedules/${id}`, {
        method: "PUT",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [DELETE] /api/tenant/my-availability/work-schedules/{id} */
export async function deleteMyWorkSchedule(id: number) {
    return request<API.ResultObject<{ deleted: boolean }>>(`${myPrefix}/work-schedules/${id}`, {
        method: "DELETE",
        withToken: true,
    }).then((res) => res.data);
}

/** [POST] /api/tenant/my-availability/time-off — files a request (status pending) */
export async function requestMyTimeOff(data: TimeOffPayload) {
    return request<API.ResultObject<TechnicianTimeOff>>(`${myPrefix}/time-off`, {
        method: "POST",
        data,
        withToken: true,
    }).then((res) => res.data);
}

/** [DELETE] /api/tenant/my-availability/time-off/{id} — withdraws a pending request */
export async function cancelMyTimeOff(id: number) {
    return request<API.ResultObject<{ deleted: boolean }>>(`${myPrefix}/time-off/${id}`, {
        method: "DELETE",
        withToken: true,
    }).then((res) => res.data);
}

/**
 * What the shared AvailabilityPanel needs to read and write one user's
 * availability. `manageAvailabilityApi(userId)` binds the manager endpoints
 * (Users list drawer); `myAvailabilityApi` binds the self-service ones.
 * Optional members are only available in manage mode.
 */
export interface AvailabilityApi {
    get: () => Promise<TechnicianAvailability>;
    addWorkSchedule: (data: WorkSchedulePayload) => Promise<unknown>;
    updateWorkSchedule: (id: number, data: Partial<WorkSchedulePayload>) => Promise<unknown>;
    deleteWorkSchedule: (id: number) => Promise<unknown>;
    /** manage: adds an approved entry; self: files a pending request */
    addTimeOff: (data: TimeOffPayload) => Promise<unknown>;
    /** manage: deletes; self: cancels a pending request */
    deleteTimeOff: (id: number) => Promise<unknown>;
    updateTimeOff?: (id: number, data: Partial<TimeOffPayload>) => Promise<unknown>;
    reviewTimeOff?: (id: number, action: "approve" | "decline", note?: string | null) => Promise<unknown>;
    updateMaxJobs?: (maxJobs?: number | null, maxJobsByType?: Record<string, number | null>) => Promise<unknown>;
    addMaxJobsOverride?: (data: MaxJobsOverridePayload) => Promise<unknown>;
    updateMaxJobsOverride?: (id: number, data: Partial<MaxJobsOverridePayload>) => Promise<unknown>;
    deleteMaxJobsOverride?: (id: number) => Promise<unknown>;
}

export function manageAvailabilityApi(userId: number): AvailabilityApi {
    return {
        get: () => getTechnicianAvailability(userId),
        addWorkSchedule: (data) => addWorkSchedule(userId, data),
        updateWorkSchedule: (id, data) => updateWorkSchedule(userId, id, data),
        deleteWorkSchedule: (id) => deleteWorkSchedule(userId, id),
        addTimeOff: (data) => addTimeOff(userId, data),
        updateTimeOff: (id, data) => updateTimeOff(userId, id, data),
        deleteTimeOff: (id) => deleteTimeOff(userId, id),
        reviewTimeOff: (id, action, note) => reviewTimeOff(userId, id, action, note),
        updateMaxJobs: (maxJobs, byType) => updateMaxJobs(userId, maxJobs, byType),
        addMaxJobsOverride: (data) => addMaxJobsOverride(userId, data),
        updateMaxJobsOverride: (id, data) => updateMaxJobsOverride(userId, id, data),
        deleteMaxJobsOverride: (id) => deleteMaxJobsOverride(userId, id),
    };
}

export const myAvailabilityApi: AvailabilityApi = {
    get: getMyAvailability,
    addWorkSchedule: addMyWorkSchedule,
    updateWorkSchedule: updateMyWorkSchedule,
    deleteWorkSchedule: deleteMyWorkSchedule,
    addTimeOff: requestMyTimeOff,
    deleteTimeOff: cancelMyTimeOff,
};

/** Fired when a request is filed from outside My Availability (profile menu) so an open page reloads */
export const MY_TIME_OFF_REQUESTED_EVENT = "app:my-time-off-requested";

export function emitMyTimeOffRequested() {
    window.dispatchEvent(new Event(MY_TIME_OFF_REQUESTED_EVENT));
}

export function onMyTimeOffRequested(handler: () => void) {
    window.addEventListener(MY_TIME_OFF_REQUESTED_EVENT, handler);
    return () => window.removeEventListener(MY_TIME_OFF_REQUESTED_EVENT, handler);
}

/** [POST] /api/tenant/technician-availability/apply-defaults */
export async function applyAvailabilityDefaults() {
    return request<API.ResultObject<{ technicians_updated: number }>>(`${urlPrefix}/apply-defaults`, {
        method: "POST",
        withToken: true,
    }).then((res) => res.data);
}

/** [GET] /api/tenant/technician-availability/calendar */
export async function getAvailabilityCalendar(params: {
    user_ids: "all" | string; // "all" or comma-separated ids
    /** Only with user_ids "all": which users it expands to. Defaults to technicians (employee). */
    user_type?: "all" | "employee" | "tenant";
    start: string; // Y-m-d
    end: string; // Y-m-d
    include_jobs?: boolean;
    /** When given, the response also says which job type limits this job counts toward */
    dispatch_id?: number;
}) {
    return request<API.ResultObject<{
        technicians: TechnicianCalendar[];
        job?: { dispatch_id: number; limit_keys: string[] };
    }>>(`${urlPrefix}/calendar`, {
        method: "GET",
        params: { ...params, include_jobs: params.include_jobs ? 1 : 0 },
        withToken: true,
        paramsSerializer,
    }).then((res) => res.data);
}

export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const DEFAULT_AVAILABILITY: TechnicianAvailabilityDefaults = {
    max_jobs_per_day: 8,
    work_time_from: "08:00",
    work_time_to: "17:00",
    work_days: [1, 2, 3, 4, 5],
};
